import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useGameEngine } from "../hooks/useGameEngine";
import { isInIsrael } from "../lib/geo";
import { recordPlayedSong, getExcludedSongIds, resetCycleIfNeeded, getUsedStartSecs, getCurrentCycle } from "../lib/playedSongs";
import { trackEvent } from "../lib/clarityEvents";
import { useLocale } from "../lib/i18n";
import {
  T,
  Mark,
  ScoreChip,
  WaveForm,
  PlaylistTile,
  BigPlayButton,
  ActionChip,
  LetterBank,
  letterBankMatches,
} from "../components/v2";

const TOTAL_STAGES = 6;

interface Playlist {
  id: string;
  name: string;
  name_en: string | null;
  theme: string;
  description: string;
  description_en: string | null;
  song_count: number;
  type: "curated" | "auto";
}

type PageState = "browse" | "endless" | "playlist-game";

interface PlaylistBrowserProps {
  onNavigate?: (tab: string) => void;
  /** When set, auto-jump into that playlist's game once playlists load.
   *  Used by Home's discover tiles to open a specific playlist directly
   *  instead of dumping the user on the browse page. The parent should
   *  clear it after consumption. */
  initialPlaylistId?: string | null;
  onInitialPlaylistConsumed?: () => void;
}

export function PlaylistBrowser({
  initialPlaylistId,
  onInitialPlaylistConsumed,
}: PlaylistBrowserProps = {}) {
  const { t, locale } = useLocale();
  const [page, setPage] = useState<PageState>("browse");
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    // Hide thin playlists — a guessing game needs a pool. Below this, the
    // user exhausts it in one or two sessions.
    const MIN_SONGS = 50;
    supabase.from("playlists").select("*").order("type", { ascending: false }).order("name").then(({ data, error }) => {
      if (error) { setLoadError(t("jukebox.loadError")); return; }
      setPlaylists((data ?? []).filter((p: Playlist) => (p.song_count ?? 0) >= MIN_SONGS));
    });
  }, []);

  // Auto-open a specific playlist when the parent passed an id (e.g. Home
  // discover tile clicked). Consume the id so back-out doesn't re-trigger.
  useEffect(() => {
    if (!initialPlaylistId || !playlists.length) return;
    const target = playlists.find((p) => p.id === initialPlaylistId);
    if (target) {
      setSelectedPlaylist(target);
      setPage("playlist-game");
    }
    onInitialPlaylistConsumed?.();
  }, [initialPlaylistId, playlists, onInitialPlaylistConsumed]);

  if (page === "endless") {
    return <EndlessMode onBack={() => setPage("browse")} />;
  }

  if (page === "playlist-game" && selectedPlaylist) {
    return <PlaylistGame playlist={selectedPlaylist} popularityThreshold={null} onBack={() => setPage("browse")} />;
  }

  const dir = locale === "he" ? "rtl" : "ltr";
  const he = locale === "he";


  return (
    <div
      dir={dir}
      className="px-5 pt-1 pb-2 nig-stagger"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Title */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontFamily: T.serif,
            fontSize: 26,
            fontWeight: 800,
            color: T.cream,
            letterSpacing: "-0.01em",
            lineHeight: 1.05,
          }}
        >
          {he ? "פלייליסטים" : "Playlists"}
        </div>
        <div style={{ fontSize: 11.5, color: T.mist, marginTop: 4 }}>
          {he ? "בחר אוסף · נגן לבד או עם חברים" : "Pick a collection · play alone or with friends"}
        </div>
      </div>

      {loadError && (
        <p style={{ color: "#ff8a8a", fontSize: 13, marginBottom: 8 }}>
          {loadError}
        </p>
      )}

      {/* Grid: endless tile first, then curated playlists */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <PlaylistTile
          color={THEME_VISUAL.endless.color}
          accent={THEME_VISUAL.endless.accent}
          title={he ? "אינסופי" : "Endless"}
          sub={he ? "ניגונים אקראיים, בלי גבול" : "random, no limit"}
          height={116}
          onClick={() => setPage("endless")}
        />
        {playlists.map((pl) => {
          const visual = THEME_VISUAL[pl.theme] ?? THEME_VISUAL.default;
          return (
            <PlaylistTile
              key={pl.id}
              color={visual.color}
              accent={visual.accent}
              title={he ? pl.name : pl.name_en ?? pl.name}
              sub={(he ? pl.description : pl.description_en ?? pl.description) ?? undefined}
              badge={NEW_THEMES.has(pl.theme) ? (he ? "חדש" : "new") : visual.badge}
              height={116}
              onClick={() => {
                setSelectedPlaylist(pl);
                setPage("playlist-game");
              }}
            />
          );
        })}
        {playlists.length === 0 && !loadError &&
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 116,
                borderRadius: T.r2,
                background: `linear-gradient(155deg, ${T.navyMid} 0%, ${T.navyDeep} 100%)`,
                border: `1px solid ${T.hairline}`,
                opacity: 0.5,
              }}
            />
          ))}
      </div>
    </div>
  );
}

// Theme → vinyl-cover gradient + accent color. Mirrors Home's discover map
// so the same playlist looks identical wherever it appears.
const THEME_VISUAL: Record<string, { color: string; accent: string; badge?: string }> = {
  chassidic:      { color: "#1a3868", accent: T.gold },
  "yamim-noraim": { color: "#3b1f5c", accent: "#c39bff", badge: undefined },
  simcha:         { color: "#5c2a2a", accent: "#f2a99c" },
  shabbos:        { color: "#5c2a2a", accent: "#f2a99c" },
  modern:         { color: "#1f4d3a", accent: "#7fd4a3" },
  kids:           { color: "#1a3868", accent: T.gold },
  kleilim:        { color: "#5c4a1f", accent: T.goldGlow },
  vocali:         { color: "#2a4868", accent: "#9fc8ff" },
  endless:        { color: T.navyMid, accent: T.gold },
  default:        { color: T.navyMid, accent: T.gold },
};

const NEW_THEMES = new Set(["kleilim", "vocali"]);


// ── Endless Mode ──

async function saveEndlessScore(songs: number, correct: number, score: number, streak: number) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase.from("endless_scores").insert({
    user_id: user.id,
    songs_played: songs,
    songs_correct: correct,
    total_score: score,
    longest_streak: streak,
  });
}

const endlessOptions = { revealMode: "notes" as const };
function EndlessMode({ onBack }: { onBack: () => void }) {
  const { t, locale } = useLocale();
  const engine = useGameEngine(endlessOptions);
  const [songCount, setSongCount] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [phase, setPhase] = useState<"loading" | "playing" | "revealed">("loading");
  const [currentHe, setCurrentHe] = useState<{ title_he: string | null; artist_he: string | null; title_en: string | null; artist_en: string | null } | null>(null);
  const [wrongSignal, setWrongSignal] = useState(0);
  const [artistRevealed, setArtistRevealed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  // Same scoring scheme as Daily: base 90, -10 per note bought, -30 for the
  // one-shot artist hint. See DailyChallenge for the full rationale.
  const BASE_SCORE = 90;
  const PER_NOTE_PENALTY = 10;
  const ARTIST_HINT_COST = 30;

  const loadNextSong = async () => {
    setPhase("loading");
    setGuesses([]);
    setArtistRevealed(false);
    setHintPenalty(0);

    const includeIsraeli = await isInIsrael();
    let excludeIds = getExcludedSongIds("endless");
    let cycle = getCurrentCycle("endless");

    const RECENT_TITLES_KEY = "nigunim:recentTitles";
    const recentTitles: string[] = JSON.parse(
      sessionStorage.getItem(RECENT_TITLES_KEY) || "[]"
    );

    // Get a random song; retry up to 4× to dodge titles seen in the last few
    // rounds (the catalog has 15 different "ה' מלך" recordings — without this
    // the player gets the same letter-bank puzzle two visits in a row).
    let chosen: { song_id: string; title: string; artist: string; itunes_id?: string | null } | null = null;
    let lastData: typeof chosen | null = null;
    for (let i = 0; i < 4; i++) {
      const { data, error } = await supabase.rpc("get_random_song", {
        exclude_ids: excludeIds,
        include_israeli: includeIsraeli,
      });
      if (error || !data?.length) break;
      lastData = data[0];
      const t = data[0].title;
      if (!recentTitles.includes(t)) {
        chosen = data[0];
        break;
      }
    }

    if (!chosen) {
      // Pool was either empty or every fresh pick re-hit a recent title.
      // Fall through with whatever we got last; or reset cycle if truly empty.
      if (!lastData) {
        cycle = resetCycleIfNeeded("endless", excludeIds.length);
        excludeIds = [];
        const retry = await supabase.rpc("get_random_song", { exclude_ids: [], include_israeli: includeIsraeli });
        if (!retry.data?.length) return;
        chosen = retry.data[0];
      } else {
        chosen = lastData;
      }
    }

    if (!chosen) return;
    await loadSong(chosen, cycle);
  };

  const loadSong = async (song: { song_id: string; title: string; artist: string; itunes_id?: string | null }, cycle: number) => {
    // Try up to 3 times with different songs if iTunes doesn't have it
    let excludeIds = getExcludedSongIds("endless");
    const RECENT_TITLES_KEY = "nigunim:recentTitles";
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const usedSecs = getUsedStartSecs(song.song_id, "endless");
        const startMode = usedSecs.length > 0
          ? { kind: "random-avoid" as const, excludeSecs: usedSecs, minDistance: 5 }
          : { kind: "random" as const };
        await engine.loadAndPlay(song.artist, song.title, startMode, song.itunes_id ?? undefined);
        const startSec = engine.track?.startSec ?? 0;
        recordPlayedSong(song.song_id, "endless", startSec, cycle);
        supabase.from("songs").select("title_he, artist_he, title_en, artist_en").eq("id", song.song_id).maybeSingle()
          .then(({ data }) => {
            const he = data?.title_he ?? null;
            setCurrentHe(data ? { title_he: he, artist_he: data.artist_he ?? null, title_en: data.title_en ?? null, artist_en: data.artist_en ?? null } : null);
            // Push to recent-titles ring so the next pick avoids the same puzzle.
            const titleKey = he ?? song.title;
            const recent: string[] = JSON.parse(sessionStorage.getItem(RECENT_TITLES_KEY) || "[]");
            const updated = [titleKey, ...recent.filter((t) => t !== titleKey)].slice(0, 5);
            sessionStorage.setItem(RECENT_TITLES_KEY, JSON.stringify(updated));
          });
        setPhase("playing");
        trackEvent("game_start");
        return;
      } catch {
        // Song not found on iTunes — get another one
        excludeIds = [...excludeIds, song.song_id];
        const il = await isInIsrael();
        const { data } = await supabase.rpc("get_random_song", { exclude_ids: excludeIds, include_israeli: il });
        if (!data?.length) break;
        song = data[0];
      }
    }

    // All attempts failed — go back
    onBack();
  };

  useEffect(() => {
    loadNextSong();
  }, []);

  const handleGuess = async (title: string) => {
    if (!engine.track) return;
    const candidates = [
      engine.track.track.title,
      currentHe?.title_he,
      currentHe?.title_en,
    ].filter((s): s is string => !!s);
    const correctMatch = candidates.some((c) => letterBankMatches(c, title));

    if (correctMatch) {
      setGuesses([...guesses, "correct"]);
      const notesBought = Math.max(0, engine.stage - 1);
      const earned = Math.max(
        0,
        BASE_SCORE - notesBought * PER_NOTE_PENALTY - hintPenalty
      );
      setTotalScore((s) => s + earned);
      setCorrect((c) => c + 1);
      setSongCount((c) => c + 1);
      setStreak((s) => { const n = s + 1; setMaxStreak((m) => Math.max(m, n)); return n; });
      setPhase("revealed");
      await engine.reveal();
    } else {
      // Wrong placement is free — does not advance the audio stage.
      setGuesses([...guesses, "wrong"]);
      setWrongSignal((s) => s + 1);
    }
  };

  const handleSkip = async () => {
    const newGuesses = [...guesses, "skip"];
    setGuesses(newGuesses);
    const hasNext = await engine.skip();
    if (!hasNext) {
      setStreak(0);
      setSongCount((c) => c + 1);
      setPhase("revealed");
      await engine.reveal();
    }
  };

  const handleArtistHint = () => {
    if (artistRevealed) return;
    setArtistRevealed(true);
    setHintPenalty((p) => p + ARTIST_HINT_COST);
  };

  const isPlaying = engine.playback.status === "playing";
  const dir = locale === "he" ? "rtl" : "ltr";
  const he = locale === "he";

  const revealedTitle = engine.track
    ? he
      ? currentHe?.title_he ?? engine.track.track.title
      : currentHe?.title_en ?? engine.track.track.title
    : "";
  const revealedArtist = engine.track
    ? he
      ? currentHe?.artist_he ?? engine.track.track.artist
      : currentHe?.artist_en ?? engine.track.track.artist
    : "";

  return (
    <div
      dir={dir}
      className="px-5 pt-1 pb-2"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Top: back + counter + streak dot indicator */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={async () => {
            await saveEndlessScore(songCount, correct, totalScore, maxStreak);
            onBack();
          }}
          aria-label={he ? "חזרה" : "Back"}
          className="nig-press nig-tap"
          style={{
            width: 36,
            height: 36,
            borderRadius: 99,
            background: "transparent",
            border: `1px solid ${T.hairline}`,
            display: "grid",
            placeItems: "center",
            padding: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d={he ? "M11 7 L7 3 M11 7 L7 11 M11 7 H2" : "M3 7 L7 3 M3 7 L7 11 M3 7 H12"}
              stroke={T.cream}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10.5, color: T.gold, letterSpacing: "0.18em", fontWeight: 600 }}>
            {he ? "מצב אינסופי" : "Endless"}
          </div>
          <div style={{ fontFamily: T.serif, fontSize: 14, fontWeight: 600, color: T.cream, marginTop: 2 }}>
            {he ? `ניגון ${songCount + 1}` : `Niggun ${songCount + 1}`}
          </div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      {/* Stats row */}
      <div
        className="flex items-center justify-around"
        style={{
          padding: "8px 0 14px",
          borderBottom: `1px solid ${T.hairline}`,
          marginBottom: 14,
        }}
      >
        <ScoreChip
          icon={<Mark kind="streak" size={14} color={T.goldGlow} />}
          value={streak}
          label={he ? "רצף" : "streak"}
        />
        <ScoreChip value={totalScore.toLocaleString()} label={he ? "ניקוד" : "score"} accent />
        <ScoreChip value={`${engine.stage}/${TOTAL_STAGES}`} label={he ? "תווים" : "notes"} />
      </div>

      {/* Stage / loading / revealed body */}
      {phase === "loading" ? (
        <div className="flex flex-col items-center" style={{ paddingTop: 60 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: `3px solid ${T.hairline}`,
              borderTopColor: T.gold,
              animation: "nig-rotate-slow 0.8s linear infinite",
            }}
          />
        </div>
      ) : phase === "revealed" && engine.track ? (
        <div className="flex flex-col items-center" style={{ paddingTop: 16 }}>
          <div
            style={{
              fontFamily: T.serif,
              fontSize: 26,
              fontWeight: 800,
              color: T.cream,
              textAlign: "center",
              lineHeight: 1.1,
              marginBottom: 6,
            }}
          >
            {revealedTitle}
          </div>
          <div style={{ fontSize: 13, color: T.gold, marginBottom: 14 }}>{revealedArtist}</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
            {Array.from({ length: TOTAL_STAGES }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 22,
                  height: 6,
                  borderRadius: 99,
                  background: i < engine.stage ? T.gold : T.hairline,
                }}
              />
            ))}
          </div>
          <button
            onClick={() => loadNextSong()}
            className="nig-press"
            style={{
              background: T.gold,
              color: T.navyDeep,
              border: 0,
              padding: "12px 28px",
              borderRadius: 99,
              fontFamily: T.sans,
              fontSize: 14,
              fontWeight: 700,
              boxShadow: T.shadowGold,
              cursor: "pointer",
            }}
          >
            {t("common.nextSong")}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center">
            <BigPlayButton
              isPlaying={isPlaying}
              loading={engine.loading}
              onToggle={() => (isPlaying ? engine.pause() : engine.play())}
            />
            <div style={{ height: 50, width: "100%", marginTop: 10, marginBottom: 6 }}>
              <WaveForm bars={28} height={50} active={isPlaying} color={T.gold} />
            </div>
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: T.gold,
              textAlign: "center",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {(() => {
              const potential = Math.max(
                0,
                BASE_SCORE - (engine.stage - 1) * PER_NOTE_PENALTY - hintPenalty
              );
              return he
                ? `${potential}+ נק׳ אם נחשתם עכשיו`
                : `+${potential} pts if you guess now`;
            })()}
          </div>
          {artistRevealed && currentHe && (
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 13,
                fontWeight: 700,
                color: T.gold,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              {he ? "זמר: " : "Artist: "}
              {he
                ? currentHe.artist_he ?? engine.track?.track.artist ?? ""
                : currentHe.artist_en ?? engine.track?.track.artist ?? ""}
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <LetterBank
              correctText={
                he
                  ? currentHe?.title_he ?? engine.track?.track.title ?? ""
                  : currentHe?.title_en ?? engine.track?.track.title ?? ""
              }
              locale={he ? "he" : "en"}
              onSubmit={handleGuess}
              wrongSignal={wrongSignal}
              disabled={!engine.ready}
            />
          </div>
          <div className="flex" style={{ gap: 8 }}>
            <ActionChip
              icon={<Mark kind="spark" size={14} color={T.gold} strokeWidth={1.6} />}
              label={
                engine.stage >= TOTAL_STAGES
                  ? he
                    ? "גלו את הניגון"
                    : "Reveal niggun"
                  : he
                  ? "תו נוסף"
                  : "More note"
              }
              sub={
                engine.stage >= TOTAL_STAGES
                  ? he
                    ? "0 נק׳"
                    : "0 pts"
                  : he
                  ? "‎-10 נק׳"
                  : "−10 pts"
              }
              onClick={handleSkip}
            />
            <ActionChip
              icon={<Mark kind="ear" size={14} color={artistRevealed ? T.mist : T.gold} strokeWidth={1.6} />}
              label={he ? "רמז: שם הזמר" : "Hint: artist"}
              sub={
                artistRevealed
                  ? he ? "נחשף" : "revealed"
                  : `‎-${ARTIST_HINT_COST} ${he ? "נק׳" : "pts"}`
              }
              muted={artistRevealed}
              onClick={artistRevealed ? undefined : handleArtistHint}
            />
          </div>
        </>
      )}

      {engine.error && (
        <p className="mt-2 text-center" style={{ color: "#ff8a8a", fontSize: 13 }}>
          {engine.error}
        </p>
      )}
    </div>
  );
}

// ── Playlist Game Mode ──

function PlaylistGame({ playlist, popularityThreshold, onBack }: { playlist: Playlist; popularityThreshold: number | null; onBack: () => void }) {
  const { t, locale } = useLocale();
  const playlistName = locale === "he" ? playlist.name : (playlist.name_en ?? playlist.name);
  const engine = useGameEngine(endlessOptions);
  const [songs, setSongs] = useState<{ id: string; title: string; artist: string; itunes_id: string | null; title_he?: string | null; artist_he?: string | null; title_en?: string | null; artist_en?: string | null }[]>([]);
  const [currentSong, setCurrentSong] = useState<{ title_he?: string | null; artist_he?: string | null; title_en?: string | null; artist_en?: string | null } | null>(null);
  const [songsPlayedCount, setSongsPlayedCount] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [phase, setPhase] = useState<"loading" | "playing" | "revealed" | "done" | "error">("loading");
  const [wrongSignal, setWrongSignal] = useState(0);
  const [artistRevealed, setArtistRevealed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  // Same scoring scheme as Daily: base 90, -10 per note bought, -30 for the
  // one-shot artist hint. See DailyChallenge for the full rationale.
  const BASE_SCORE = 90;
  const PER_NOTE_PENALTY = 10;
  const ARTIST_HINT_COST = 30;

  const context = `playlist:${playlist.id}`;

  const tryLoadSong = async (allSongs: { id: string; title: string; artist: string; itunes_id: string | null; title_he?: string | null; artist_he?: string | null; title_en?: string | null; artist_en?: string | null }[], maxAttempts = 10) => {
    const triedIds: string[] = [];
    // Many liturgical titles repeat across artists ("ה' מלך" appears 15× in
    // the catalog as different recordings). Without this filter the player
    // sees the same letter-bank puzzle on consecutive plays and thinks the
    // pick isn't random. Exclude any title heard in the last few rounds.
    const RECENT_TITLES_KEY = "nigunim:recentTitles";
    const recentTitles: string[] = JSON.parse(
      sessionStorage.getItem(RECENT_TITLES_KEY) || "[]"
    );
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const excludeIds = getExcludedSongIds(context);
      const available = allSongs.filter(
        (s) =>
          !excludeIds.includes(s.id) &&
          !triedIds.includes(s.id) &&
          !recentTitles.includes(s.title_he ?? s.title)
      );
      if (available.length === 0 && triedIds.length >= allSongs.length) break; // truly exhausted
      const pool = available.length > 0 ? available : allSongs.filter((s) => !triedIds.includes(s.id));
      if (pool.length === 0) break;
      const cycle = getCurrentCycle(context);
      const song = pool[Math.floor(Math.random() * pool.length)];

      const usedSecs = getUsedStartSecs(song.id, context);
      const startMode = usedSecs.length > 0
        ? { kind: "random-avoid" as const, excludeSecs: usedSecs, minDistance: 5 }
        : { kind: "random" as const };

      // Use loadTrack (not loadAndPlay) to avoid autoplay issues on iOS
      let loadedTrack;
      try {
        loadedTrack = await engine.loadTrack(song.artist, song.title, startMode, song.itunes_id ?? undefined);
      } catch {
        triedIds.push(song.id);
        continue;
      }

      if (loadedTrack) {
        recordPlayedSong(song.id, context, loadedTrack.startSec ?? 0, cycle);
        setCurrentSong({ title_he: song.title_he, artist_he: song.artist_he, title_en: song.title_en, artist_en: song.artist_en });
        // Push title to the session-level recent-titles ring (max 5).
        const titleKey = song.title_he ?? song.title;
        const updated = [titleKey, ...recentTitles.filter((t) => t !== titleKey)].slice(0, 5);
        sessionStorage.setItem(RECENT_TITLES_KEY, JSON.stringify(updated));
        return true;
      }
      triedIds.push(song.id);
    }
    return false;
  };

  useEffect(() => {
    // Load playlist songs from Supabase (paginate to handle >1000 songs)
    const loadAllSongs = async () => {
      const allData: any[] = [];
      const PAGE_SIZE = 1000;
      let offset = 0;
      while (true) {
        let query = supabase
          .from("playlist_songs")
          .select("song_id, position, songs!inner(id, title, artist, itunes_id, popularity, title_he, artist_he, title_en, artist_en)")
          .eq("playlist_id", playlist.id)
          .order("position")
          .range(offset, offset + PAGE_SIZE - 1);
        if (popularityThreshold) {
          query = query.gte("songs.popularity", popularityThreshold);
        }
        const { data } = await query;
        if (!data?.length) break;
        allData.push(...data);
        if (data.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }

      if (!allData.length) return;
      const mapped = allData
        .filter((d: any) => d.songs) // skip orphaned references
        .map((d: any) => ({
          id: d.songs.id,
          title: d.songs.title,
          artist: d.songs.artist,
          itunes_id: d.songs.itunes_id ?? null,
          title_he: d.songs.title_he ?? null,
          artist_he: d.songs.artist_he ?? null,
          title_en: d.songs.title_en ?? null,
          artist_en: d.songs.artist_en ?? null,
        }));
      setSongs(mapped);

      setGuesses([]);
      setPhase("loading");
      const loaded = await tryLoadSong(mapped);
      setPhase(loaded ? "playing" : "error");
      if (loaded) trackEvent("game_start");
    };
    loadAllSongs();
  }, [playlist.id]);

  const handleSongDone = (score: number) => {
    setTotalScore((s) => s + score);
    if (score > 0) setCorrect((c) => c + 1);
    setSongsPlayedCount((c) => c + 1);
    setPhase("revealed");
  };

  const handleNext = async () => {
    if (songsPlayedCount + 1 >= songs.length) {
      setPhase("done");
      return;
    }

    setGuesses([]);
    setArtistRevealed(false);
    setHintPenalty(0);
    setPhase("loading");
    const loaded = await tryLoadSong(songs);
    setPhase(loaded ? "playing" : "error");
  };

  const handleGuess = async (title: string) => {
    if (!engine.track) return;
    const candidates = [
      engine.track.track.title,
      currentSong?.title_he,
      currentSong?.title_en,
    ].filter((s): s is string => !!s);
    const correctMatch = candidates.some((c) => letterBankMatches(c, title));

    if (correctMatch) {
      setGuesses([...guesses, "correct"]);
      const notesBought = Math.max(0, engine.stage - 1);
      const earned = Math.max(
        0,
        BASE_SCORE - notesBought * PER_NOTE_PENALTY - hintPenalty
      );
      await engine.reveal();
      handleSongDone(earned);
    } else {
      const newGuesses = [...guesses, "wrong"];
      setGuesses(newGuesses);
      setWrongSignal((s) => s + 1);
      const hasNext = await engine.skip();
      if (!hasNext) {
        await engine.reveal();
        handleSongDone(0);
      }
    }
  };

  const handleSkip = async () => {
    const newGuesses = [...guesses, "skip"];
    setGuesses(newGuesses);
    const hasNext = await engine.skip();
    if (!hasNext) {
      await engine.reveal();
      handleSongDone(0);
    }
  };

  const handleArtistHint = () => {
    if (artistRevealed) return;
    setArtistRevealed(true);
    setHintPenalty((p) => p + ARTIST_HINT_COST);
  };

  const isPlaying = engine.playback.status === "playing";
  const dir = locale === "he" ? "rtl" : "ltr";
  const he = locale === "he";

  const revealedTitle = engine.track
    ? he
      ? currentSong?.title_he ?? engine.track.track.title
      : currentSong?.title_en ?? engine.track.track.title
    : "";
  const revealedArtist = engine.track
    ? he
      ? currentSong?.artist_he ?? engine.track.track.artist
      : currentSong?.artist_en ?? engine.track.track.artist
    : "";
  const accuracyPct = songsPlayedCount > 0
    ? Math.round((correct / songsPlayedCount) * 100)
    : 0;

  if (phase === "error") {
    const handleRetry = async () => {
      setPhase("loading");
      const loaded = await tryLoadSong(songs);
      setPhase(loaded ? "playing" : "error");
    };
    return (
      <div
        dir={dir}
        className="px-5 pt-4 pb-2 flex flex-col items-center"
        style={{ color: T.cream, fontFamily: T.sans, gap: 14 }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              color: T.gold,
              fontWeight: 600,
            }}
          >
            {playlistName}
          </div>
          <h2
            style={{
              fontFamily: T.serif,
              fontSize: 22,
              fontWeight: 800,
              color: T.cream,
              marginTop: 6,
            }}
          >
            {t("jukebox.songUnavailable")}
          </h2>
          <p style={{ fontSize: 12.5, color: T.mist, marginTop: 8, lineHeight: 1.5, maxWidth: 280 }}>
            {t("jukebox.songUnavailableDesc")}
          </p>
        </div>
        <button
          onClick={handleRetry}
          className="nig-press"
          style={{
            background: T.gold,
            color: T.navyDeep,
            border: 0,
            padding: "12px 26px",
            borderRadius: 99,
            fontFamily: T.sans,
            fontSize: 14,
            fontWeight: 700,
            boxShadow: T.shadowGold,
            cursor: "pointer",
          }}
        >
          {t("common.tryAgain")}
        </button>
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            color: T.mist,
            border: 0,
            fontSize: 12.5,
            cursor: "pointer",
          }}
        >
          {t("jukebox.backToPlaylists")}
        </button>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div
        dir={dir}
        className="px-5 pt-4 pb-2 flex flex-col items-center"
        style={{ color: T.cream, fontFamily: T.sans, gap: 16 }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", color: T.gold, fontWeight: 600 }}>
            {playlistName}
          </div>
          <h2
            style={{
              fontFamily: T.serif,
              fontSize: 26,
              fontWeight: 800,
              color: T.cream,
              marginTop: 6,
            }}
          >
            {t("jukebox.complete")}
          </h2>
        </div>
        <div
          className="w-full"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
            padding: "14px 8px",
            background: `${T.navyMid}cc`,
            border: `1px solid ${T.gold}30`,
            borderRadius: T.r2,
            backdropFilter: "blur(8px)",
          }}
        >
          <ScoreChip value={totalScore.toLocaleString()} label={t("common.score")} accent />
          <ScoreChip value={String(correct)} label={t("jukebox.correct")} />
          <ScoreChip value={`${accuracyPct}%`} label={he ? "דיוק" : "accuracy"} />
        </div>
        <button
          onClick={onBack}
          className="nig-press w-full"
          style={{
            background: T.gold,
            color: T.navyDeep,
            border: 0,
            padding: "13px",
            borderRadius: 99,
            fontFamily: T.sans,
            fontSize: 14,
            fontWeight: 700,
            boxShadow: T.shadowGold,
            cursor: "pointer",
          }}
        >
          {t("jukebox.backToPlaylists")}
        </button>
      </div>
    );
  }

  return (
    <div
      dir={dir}
      className="px-5 pt-1 pb-2"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Top: back + playlist name + progress dots */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onBack}
          aria-label={he ? "חזרה" : "Back"}
          className="nig-press nig-tap"
          style={{
            width: 36,
            height: 36,
            borderRadius: 99,
            background: "transparent",
            border: `1px solid ${T.hairline}`,
            display: "grid",
            placeItems: "center",
            padding: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d={he ? "M11 7 L7 3 M11 7 L7 11 M11 7 H2" : "M3 7 L7 3 M3 7 L7 11 M3 7 H12"}
              stroke={T.cream}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
        <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              color: T.gold,
              letterSpacing: "0.18em",
              fontWeight: 600,
            }}
          >
            {he ? "פלייליסט" : "Playlist"}
          </div>
          <div
            style={{
              fontFamily: T.serif,
              fontSize: 14,
              fontWeight: 600,
              color: T.cream,
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {playlistName}
          </div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      {/* Stats row — no total-songs reveal: just current niggun index */}
      <div
        className="flex items-center justify-around"
        style={{
          padding: "8px 0 14px",
          borderBottom: `1px solid ${T.hairline}`,
          marginBottom: 14,
        }}
      >
        <ScoreChip
          value={`#${songsPlayedCount + 1}`}
          label={he ? "ניגון" : "niggun"}
        />
        <ScoreChip value={totalScore.toLocaleString()} label={he ? "ניקוד" : "score"} accent />
        <ScoreChip
          value={`${engine.stage}/${TOTAL_STAGES}`}
          label={he ? "תווים" : "notes"}
        />
      </div>

      {/* Stage / loading / revealed body */}
      {phase === "loading" ? (
        <div className="flex flex-col items-center" style={{ paddingTop: 60 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              border: `3px solid ${T.hairline}`,
              borderTopColor: T.gold,
              animation: "nig-rotate-slow 0.8s linear infinite",
            }}
          />
        </div>
      ) : phase === "revealed" && engine.track ? (
        <div className="flex flex-col items-center" style={{ paddingTop: 16 }}>
          <div
            style={{
              fontFamily: T.serif,
              fontSize: 26,
              fontWeight: 800,
              color: T.cream,
              textAlign: "center",
              lineHeight: 1.1,
              marginBottom: 6,
            }}
          >
            {revealedTitle}
          </div>
          <div style={{ fontSize: 13, color: T.gold, marginBottom: 14 }}>{revealedArtist}</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
            {Array.from({ length: TOTAL_STAGES }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 22,
                  height: 6,
                  borderRadius: 99,
                  background: i < engine.stage ? T.gold : T.hairline,
                }}
              />
            ))}
          </div>
          <button
            onClick={handleNext}
            className="nig-press"
            style={{
              background: T.gold,
              color: T.navyDeep,
              border: 0,
              padding: "12px 28px",
              borderRadius: 99,
              fontFamily: T.sans,
              fontSize: 14,
              fontWeight: 700,
              boxShadow: T.shadowGold,
              cursor: "pointer",
            }}
          >
            {songsPlayedCount >= songs.length - 1 ? t("jukebox.seeResults") : t("common.nextSong")}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center">
            <BigPlayButton
              isPlaying={isPlaying}
              loading={engine.loading}
              onToggle={() => (isPlaying ? engine.pause() : engine.play())}
            />
            <div style={{ height: 50, width: "100%", marginTop: 10, marginBottom: 6 }}>
              <WaveForm bars={28} height={50} active={isPlaying} color={T.gold} />
            </div>
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: T.gold,
              textAlign: "center",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {(() => {
              const potential = Math.max(
                0,
                BASE_SCORE - (engine.stage - 1) * PER_NOTE_PENALTY - hintPenalty
              );
              return he
                ? `${potential}+ נק׳ אם נחשתם עכשיו`
                : `+${potential} pts if you guess now`;
            })()}
          </div>
          {artistRevealed && currentSong && (
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 13,
                fontWeight: 700,
                color: T.gold,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              {he ? "זמר: " : "Artist: "}
              {he
                ? currentSong.artist_he ?? engine.track?.track.artist ?? ""
                : currentSong.artist_en ?? engine.track?.track.artist ?? ""}
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <LetterBank
              correctText={
                he
                  ? currentSong?.title_he ?? engine.track?.track.title ?? ""
                  : currentSong?.title_en ?? engine.track?.track.title ?? ""
              }
              locale={he ? "he" : "en"}
              onSubmit={handleGuess}
              wrongSignal={wrongSignal}
              disabled={!engine.ready}
            />
          </div>
          <div className="flex" style={{ gap: 8 }}>
            <ActionChip
              icon={<Mark kind="spark" size={14} color={T.gold} strokeWidth={1.6} />}
              label={
                engine.stage >= TOTAL_STAGES
                  ? he
                    ? "גלו את הניגון"
                    : "Reveal niggun"
                  : he
                  ? "תו נוסף"
                  : "More note"
              }
              sub={
                engine.stage >= TOTAL_STAGES
                  ? he
                    ? "0 נק׳"
                    : "0 pts"
                  : he
                  ? "‎-10 נק׳"
                  : "−10 pts"
              }
              onClick={handleSkip}
            />
            <ActionChip
              icon={<Mark kind="ear" size={14} color={artistRevealed ? T.mist : T.gold} strokeWidth={1.6} />}
              label={he ? "רמז: שם הזמר" : "Hint: artist"}
              sub={
                artistRevealed
                  ? he ? "נחשף" : "revealed"
                  : `‎-${ARTIST_HINT_COST} ${he ? "נק׳" : "pts"}`
              }
              muted={artistRevealed}
              onClick={artistRevealed ? undefined : handleArtistHint}
            />
          </div>
        </>
      )}

      {engine.error && (
        <p className="mt-2 text-center" style={{ color: "#ff8a8a", fontSize: 13 }}>
          {engine.error}
        </p>
      )}
    </div>
  );
}
